import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { UploadSimple, FileXls, DownloadSimple, CheckCircle, WarningCircle, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const FY_OPTIONS = ["25-26", "24-25", "23-24", "22-23"];

// Indian FY: Apr 1 – Mar 31. Returns e.g. "25-26" for 2026-02-11.
function fyFromIso(iso) {
  if (!iso) return null;
  const [y, m] = iso.split("-").map(Number);
  const start = m >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}-${String(start + 1).slice(-2)}`;
}

function withinFy(fy, iso) {
  if (!fy || !iso) return true;
  return fyFromIso(iso) === fy;
}

export default function UploadPage() {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [financialYear, setFinancialYear] = useState("25-26");
  const nav = useNavigate();

  const fyOptions = Array.from(new Set([...FY_OPTIONS, financialYear])).sort().reverse();

  const onDrop = useCallback(async (files) => {
    if (!files.length) return;
    const file = files[0];
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/imports/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Auto-detect FY from the earliest valid invoice date so commit doesn't silently skip rows.
      const dates = (data.rows || [])
        .filter((r) => r.is_valid && r.invoice_date)
        .map((r) => r.invoice_date)
        .sort();
      const derived = dates.length ? fyFromIso(dates[0]) : null;
      if (derived) setFinancialYear(derived);
      toast.success(`Parsed ${data.total_rows} rows`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const commit = async () => {
    if (!preview) return;
    if (preview.valid_rows === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post(`/imports/${preview.batch_id}/commit`, null, {
        params: { financial_year: financialYear },
      });
      const skipped = (data.rows || []).filter((r) => r.skipped);
      if (!data.created_invoices) {
        const reason = skipped[0]?.reason || "no valid rows matched the selected financial year.";
        toast.error(`No invoices generated — ${reason}`, { duration: 8000 });
        return;
      }
      if (skipped.length) {
        toast.warning(`${skipped.length} row(s) skipped: ${skipped[0]?.reason || ""}`, { duration: 8000 });
      }
      toast.success(`Created ${data.created_parties} issuers · ${data.created_invoices} draft invoices`);
      nav(`/batches/${preview.batch_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const downloadSample = async () => {
    const token = localStorage.getItem("aig_access_token");
    const res = await fetch(`${API_BASE}/imports/sample`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "invoice_import_sample.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight font-bold text-slate-900">Upload Excel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Columns: Issuer Name, PAN, Total Amount, Bank Account Number, IFSC Code, Bank Name, Invoice Date.
            Bill-To is fixed to EVOPLUS IT SERVICES PRIVATE LIMITED.
          </p>
        </div>
        <Button variant="outline" onClick={downloadSample} data-testid="download-sample-btn">
          <DownloadSimple size={16} className="mr-2" /> Download sample
        </Button>
      </div>

      <div className="card-panel p-4 flex items-end gap-4">
        <div className="flex-1">
          <Label className="mb-1 block">Financial Year</Label>
          <Select value={financialYear} onValueChange={setFinancialYear}>
            <SelectTrigger className="w-40" data-testid="fy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground max-w-md">
          All invoice dates must fall within the selected FY (Apr 1 to Mar 31). Invoice numbers will be issued as
          <span className="font-mono ml-1">PREFIX/{financialYear}/SEQ</span>.
        </div>
      </div>

      <div
        {...getRootProps()}
        data-testid="drop-zone"
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
          isDragActive ? "bg-primary/5 border-primary" : "bg-card border-border hover:border-primary/60"
        }`}
      >
        <input {...getInputProps()} data-testid="file-input" />
        <UploadSimple size={40} weight="duotone" className="mx-auto text-primary/70" />
        <div className="mt-3 font-display text-lg">
          {isDragActive ? "Drop the file here…" : "Drag and drop your Excel file, or click to browse"}
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          Required columns: Issuer Name, PAN, Total Amount, Bank Account Number, IFSC Code, Bank Name, Invoice Date
        </div>
        {uploading && <div className="mt-4 text-sm text-primary" data-testid="uploading">Processing file…</div>}
      </div>

      {preview && (
        <div className="card-panel" data-testid="preview-panel">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileXls size={22} weight="duotone" className="text-emerald-700" />
              <div>
                <div className="font-medium">{preview.filename}</div>
                <div className="text-xs text-muted-foreground">
                  {preview.total_rows} rows ·{" "}
                  <span className="text-emerald-700">{preview.valid_rows} valid</span> ·{" "}
                  <span className="text-rose-700">{preview.invalid_rows} invalid</span>
                  {" · "}FY <span className="font-mono">{financialYear}</span>
                </div>
              </div>
            </div>
            <Button onClick={commit} disabled={committing || preview.valid_rows === 0} data-testid="commit-btn">
              {committing ? "Importing..." : "Import valid rows"} <CaretRight size={16} className="ml-1" />
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-dense text-sm" data-testid="preview-table">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left">Row</th>
                  <th className="text-left">Issuer</th>
                  <th className="text-left">PAN</th>
                  <th className="text-right">Total</th>
                  <th className="text-left">Bank</th>
                  <th className="text-left">A/C</th>
                  <th className="text-left">IFSC</th>
                  <th className="text-left">Invoice Date</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row_number} className="border-t border-border/60 hover:bg-muted/40">
                    <td className="num-cell text-left">{r.row_number}</td>
                    <td className="font-medium">{r.issuer_name || "—"}</td>
                    <td className="font-mono text-xs">{r.pan || "—"}</td>
                    <td className="num-cell">{r.total_amount ? formatInr(r.total_amount) : "—"}</td>
                    <td className="text-xs">{r.bank_name || "—"}</td>
                    <td className="font-mono text-xs">{r.bank_account || "—"}</td>
                    <td className="font-mono text-xs">{r.ifsc || "—"}</td>
                    <td className="text-xs">{r.invoice_date || "—"}</td>
                    <td>
                      {r.is_valid ? (
                        withinFy(financialYear, r.invoice_date) ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <CheckCircle size={14} /> Valid
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5" data-testid={`fy-warning-${r.row_number}`}>
                            <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                              <WarningCircle size={14} /> Date outside FY {financialYear}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Set FY to {fyFromIso(r.invoice_date)} or this row will be skipped
                            </span>
                          </div>
                        )
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 text-rose-700 text-xs">
                            <WarningCircle size={14} /> Invalid
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {r.errors?.join(" · ")}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
